// PRODUCTION — single form builder item block
import React, { useRef, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { CollectDataFormItemType } from 'src/constants/collect-data/enums'
import { ConfigRow } from '../shared/layout'
import { SoloCheckbox } from '../shared/SoloCheckbox'
import { RichTextEditor } from 'src/components/shared/form/RichTextEditor'
import { FORM_ITEM_ICONS, FORM_ITEM_META } from './formItemMeta'
import { DeleteConfirmButton } from './DeleteConfirmButton'
import { OptionLabel } from './OptionLabel'
import type { FormItemOption } from 'src/interfaces/onboardingTemplate'
import { newFormItemOption, type LocalFormItem } from './types'
import {
  formFieldInputCss,
  formFieldInputReadOnlyCss,
  formSectionCss,
  formSectionLabelCss,
  moveBtnCss,
  optionSwapBtnCss,
} from './formStepStyles'

export function FormItemBlock({
  item,
  index,
  total,
  onUpdate,
  onRemove,
  onMove,
  readOnly = false,
}: {
  item: LocalFormItem
  index: number
  total: number
  onUpdate: (patch: Partial<LocalFormItem>) => void
  onRemove: () => void
  onMove: (dir: 'up' | 'down') => void
  readOnly?: boolean
}) {
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  const addInputRef = useRef<HTMLInputElement>(null)
  const meta = FORM_ITEM_META[item.type]
  const fields = meta?.fields ?? []

  const moveBtn = (dir: 'up' | 'down', disabled: boolean) => (
    <button
      type="button"
      onClick={() => onMove(dir)}
      disabled={disabled}
      css={moveBtnCss(disabled)}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points={dir === 'up' ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
      </svg>
    </button>
  )

  const renderField = (field: string) => {
    if (field === 'Content') {
      return (
        <div key={field} css={formSectionCss}>
          <span css={formSectionLabelCss(readOnly)}>Content</span>
          <RichTextEditor
            value={item.content}
            onChange={(html) => onUpdate({ content: html })}
            readOnly={readOnly}
          />
        </div>
      )
    }

    if (field === 'Is required?' || field === 'Check answers?') {
      const key = field === 'Is required?' ? 'required' : 'checkAnswers'

      return (
        <ConfigRow key={field} label={field} disabled={readOnly}>
          <SoloCheckbox
            checked={item[key] as boolean}
            onChange={(v) => onUpdate({ [key]: v })}
            disabled={readOnly}
          />
        </ConfigRow>
      )
    }

    if (field === 'Options list') {
      const opts = item.options
      const setOpts = (newOpts: FormItemOption[]) => onUpdate({ options: newOpts })
      const showValidAnswerControls =
        item.checkAnswers &&
        (item.type === CollectDataFormItemType.Radio ||
          item.type === CollectDataFormItemType.Checkbox ||
          item.type === CollectDataFormItemType.Select)

      const handleAdd = (val: string) => {
        const trimmed = val.trim()

        if (!trimmed) {
          return
        }

        setOpts([...opts, newFormItemOption(trimmed)])
      }

      const handleSwap = (a: number, b: number) => {
        if (b < 0 || b >= opts.length) {
          return
        }

        const n = [...opts]
        ;[n[a], n[b]] = [n[b], n[a]]
        setOpts(n)
      }

      const setOptionCorrect = (index: number, correct: boolean) => {
        const n = [...opts]

        n[index] = { ...n[index], isCorrect: correct }
        setOpts(n)
      }

      return (
        <div key={field} css={[formSectionCss, tw`gap-2.5`]}>
          <span css={formSectionLabelCss(readOnly)}>Options</span>
          {showValidAnswerControls ? (
            <span css={tw`text-[12px] leading-snug text-shades-500`}>
              Select one or more valid answers for this question.
            </span>
          ) : null}
          <div css={tw`flex gap-1.5`}>
            <input
              ref={addInputRef}
              placeholder="Type option and press Enter"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd(e.currentTarget.value)
                  e.currentTarget.value = ''
                }
              }}
              css={formFieldInputCss}
            />
            <Button
              size="tiny"
              color="primary"
              onClick={() => {
                if (addInputRef.current) {
                  handleAdd(addInputRef.current.value)
                  addInputRef.current.value = ''
                  addInputRef.current.focus()
                }
              }}
              leftIcon={
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              className={tw`!h-[38px] !px-3.5`}
            >
              Add
            </Button>
          </div>
          {opts.length > 0 && (
            <div css={tw`overflow-hidden rounded-lg border border-shades-100 border-solid`}>
              {opts.map((opt, oi) => (
                <div
                  key={oi}
                  css={[
                    tw`flex items-center gap-2 bg-shades-0 px-2 py-1.5`,
                    oi < opts.length - 1 ? tw`border-b border-shades-100 border-solid` : undefined,
                  ]}
                >
                  <div css={tw`flex flex-shrink-0`}>
                    <button
                      type="button"
                      onClick={() => handleSwap(oi, oi - 1)}
                      disabled={oi === 0}
                      css={optionSwapBtnCss(oi === 0)}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSwap(oi, oi + 1)}
                      disabled={oi === opts.length - 1}
                      css={optionSwapBtnCss(oi === opts.length - 1)}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                  <OptionLabel
                    value={opt.title}
                    onChange={(v) => {
                      const n = [...opts]

                      n[oi] = { ...n[oi], title: v }
                      setOpts(n)
                    }}
                    onRemove={() => setOpts(opts.filter((_, i) => i !== oi))}
                  />
                  {showValidAnswerControls ? (
                    <label
                      css={tw`flex flex-shrink-0 cursor-pointer select-none items-center gap-1.5 pr-1`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SoloCheckbox
                        checked={Boolean(opt.isCorrect)}
                        onChange={(v) => setOptionCorrect(oi, v)}
                        disabled={readOnly}
                      />
                      <span
                        css={[
                          tw`whitespace-nowrap text-[12px] font-medium`,
                          readOnly ? tw`text-shades-400` : tw`text-shades-600`,
                        ]}
                      >
                        Valid
                      </span>
                    </label>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    if (field === 'Title') {
      return (
        <ConfigRow key={field} label="Title" disabled={readOnly}>
          <input
            value={item.title}
            readOnly={readOnly}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder="Enter title"
            css={[formFieldInputCss, readOnly ? formFieldInputReadOnlyCss : undefined]}
          />
        </ConfigRow>
      )
    }

    if (field === 'Video URL') {
      return (
        <ConfigRow key={field} label="Video URL" disabled={readOnly}>
          <input
            value={item.url}
            readOnly={readOnly}
            onChange={(e) => onUpdate({ url: e.target.value })}
            placeholder="https://..."
            css={[formFieldInputCss, readOnly ? formFieldInputReadOnlyCss : undefined]}
          />
        </ConfigRow>
      )
    }

    return null
  }

  return (
    <div css={tw`rounded-[10px] border border-shades-200 bg-shades-0 border-solid`}>
      <div css={tw`flex items-center border-b border-shades-100 px-3.5 py-2 border-solid`}>
        {!deleteConfirming && (
          <div css={tw`mr-2 flex`}>
            {moveBtn('up', index === 0 || readOnly)}
            {moveBtn('down', index === total - 1 || readOnly)}
          </div>
        )}
        {!deleteConfirming && (
          <div css={tw`flex flex-1 items-center gap-2`}>
            {FORM_ITEM_ICONS[item.type] && (
              <span css={tw`flex flex-shrink-0 text-shades-500`}>{FORM_ITEM_ICONS[item.type]}</span>
            )}
            <span css={tw`text-sm font-semibold text-shades-900`}>
              {meta?.label ?? `Item #${index + 1}`}
            </span>
          </div>
        )}
        {deleteConfirming && <div css={tw`flex-1`} />}
        <DeleteConfirmButton
          onConfirm={onRemove}
          resetKey={item.id}
          onPendingChange={setDeleteConfirming}
          disabled={readOnly}
        />
      </div>
      <div css={[tw`flex flex-col`, readOnly ? tw`pointer-events-none opacity-[0.72]` : undefined]}>
        {fields.map((field) => renderField(field))}
      </div>
    </div>
  )
}
